'use client';

import { Outlet } from 'react-router';

import { useCombinedRouterMatchesHandle } from '#/hooks/useCombinedRouterMatchesHandle';
import { useLayoutHeaderSearch } from '#/layouts/Layout/Header/useLayoutHeaderSearch';
import type { EmbedSearchWidgetApiMetadata } from '#/modules/embedSearchWidget/api/types/EmbedSearchWidgetApiMetadata';
import { useEmbedSearchWidgetResize } from '#/modules/embedSearchWidget/hooks/useEmbedSearchWidgetResize';
import { SearchRefinementsWrapper } from '#/modules/search/components/Refinements/SearchRefinementsWrapper';
import { colors } from '#/theme/theme';

import {
  EmbedSearchWidgetLayoutChildrenHolder,
  EmbedSearchWidgetLayoutHolder,
  EmbedSearchWidgetLayoutProgressBar,
  EmbedSearchWidgetLayoutProgressBarFixedHolder,
  EmbedSearchWidgetLayoutSearchRefinementsHolder,
} from './EmbedSearchWidgetLayout.styled';
import type { EmbedSearchWidgetLayoutHandleProps } from './EmbedSearchWidgetLayoutHandleProps';
import { EmbedSearchWidgetLayoutScrollRestoration } from './EmbedSearchWidgetLayoutScrollRestoration';
import { EmbedSearchWidgetLayoutSearch } from './Search/EmbedSearchWidgetLayoutSearch';

type Props = {
  embedSearchWidget: EmbedSearchWidgetApiMetadata;
};

export function EmbedSearchWidgetLayout({ embedSearchWidget }: Props) {
  useEmbedSearchWidgetResize();

  const handlePropsWithRouteMatchesHandle =
    useCombinedRouterMatchesHandle<EmbedSearchWidgetLayoutHandleProps>();

  const { darkBackground, isSearchPage, searchRefinements } =
    handlePropsWithRouteMatchesHandle;

  const {
    initialQuery,
    changeSearchLocation,
    search,
    searchLocation,
    searchOnClear,
  } = useLayoutHeaderSearch({
    defaultSearchType: embedSearchWidget.listingType,
    isSearchPage: isSearchPage,
    isSearchLandingPage: false,
    embedSearchWidgetId: embedSearchWidget.id,
    searchFromPageType: isSearchPage ? 'SEARCH' : 'LISTING',
  });

  return (
    <EmbedSearchWidgetLayoutHolder
      id="embed-search-widget"
      style={{
        background: darkBackground ? colors.backgroundGrey : undefined,
      }}
    >
      <EmbedSearchWidgetLayoutScrollRestoration />

      <EmbedSearchWidgetLayoutProgressBarFixedHolder>
        <EmbedSearchWidgetLayoutProgressBar />
      </EmbedSearchWidgetLayoutProgressBarFixedHolder>

      <EmbedSearchWidgetLayoutSearch
        initialQuery={initialQuery}
        onSearch={(data) =>
          search({
            query: data.query,
            triggeredByRemoteLocationChange:
              data.triggeredByRemoteLocationChange,
            radius: null,
            listingType: embedSearchWidget.listingType,
          })
        }
        searchOnClear={searchOnClear}
        searchLocation={searchLocation}
        onChangeSearchLocation={changeSearchLocation}
      />

      <EmbedSearchWidgetLayoutSearchRefinementsHolder>
        <SearchRefinementsWrapper hideOnMobile={false}>
          {searchRefinements}
        </SearchRefinementsWrapper>
      </EmbedSearchWidgetLayoutSearchRefinementsHolder>

      <EmbedSearchWidgetLayoutChildrenHolder>
        <Outlet />
      </EmbedSearchWidgetLayoutChildrenHolder>
    </EmbedSearchWidgetLayoutHolder>
  );
}
